import { apiRequest } from "./baseApi";
import { parseJsonOrThrow } from "./responseUtils";

/**
 * GMA-786 — Xandar Kardian per-client integration toggle.
 * Admin/super_admin write via PUT /api/v2/users/:clientId until a dedicated
 * v3 xandar-integration route lands (GMA-1418).
 */

export const setXandarKardianEnabled = async (clientId, enabled) => {
  const res = await apiRequest(`/users/${clientId}`, {
    version: "v2",
    method: "PUT",
    body: { xandarKardianEnabled: enabled },
  });
  return parseJsonOrThrow(res, "Xandar Kardian integration toggle");
};

/**
 * react-app #583 — Xandar sensor <-> client bindings.
 *
 * Routes live at /api/v3/clients/:clientId/xandar/devices (cortex-be
 * routes.js; Controllers/XandarKardianDevicesController.js). They are
 * deliberately SEPARATE from the Pontosense `iot-devices` bind path, which
 * hardcodes its own vendor.
 */

/** GET the client's Xandar sensors. Returns `{ clientId, devices }`. */
export const fetchXandarDevices = async (clientId) => {
  const res = await apiRequest(`clients/${clientId}/xandar/devices`, {
    version: "v3",
  });
  return parseJsonOrThrow(res, "Xandar devices");
};

/**
 * Bind a sensor to the client. BOTH fields are required server-side:
 * `installLocation` is one of bedroom | office | living_room | other and
 * drives caregiver presence copy ("At desk" vs "In bed"), so the backend
 * 400s without it.
 *
 * A same-client re-POST refreshes `installLocation` and re-activates an
 * unbound sensor — which is why "edit location" is this same call, not a
 * PATCH.
 */
export const bindXandarDevice = async (clientId, body) => {
  const res = await apiRequest(`clients/${clientId}/xandar/devices`, {
    method: "POST",
    body,
    version: "v3",
  });
  return parseJsonOrThrow(res, "Xandar bind device");
};

/**
 * Soft-unbind a sensor (backend sets status:inactive; the row is never
 * destroyed), so stale samples from a removed sensor stop routing.
 */
export const unbindXandarDevice = async (clientId, sensorSn) => {
  const res = await apiRequest(
    `clients/${clientId}/xandar/devices/${encodeURIComponent(sensorSn)}`,
    { method: "DELETE", version: "v3" },
  );
  return parseJsonOrThrow(res, "Xandar remove device");
};
